import { useQueryClient } from '@tanstack/react-query';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import {
  entrar as entrarNaApi,
  sair as sairNaApi,
} from '@/features/autenticacao/api/autenticacaoApi';
import {
  instalarInterceptorDeSessao,
  liberarRenovacao,
  restaurarSessao,
} from '@/features/autenticacao/renovacaoDeSessao';
import { SessaoContexto, type Sessao } from '@/features/autenticacao/SessaoContexto';
import {
  assinarSessao,
  definirSessao,
  deRespostaDeLogin,
  lerSessao,
} from '@/features/autenticacao/sessaoEmMemoria';

interface ProvedorDeSessaoProps {
  children: ReactNode;
}

/**
 * Provedor da sessao.
 *
 * Assina a store em memoria em vez de manter estado proprio. Duas copias do token —
 * uma no modulo, para o interceptor, outra em estado do React, para a interface —
 * divergiriam justamente logo depois de uma renovacao disparada por 401.
 */
export function ProvedorDeSessao({ children }: ProvedorDeSessaoProps) {
  const queryClient = useQueryClient();

  const sessao = useSyncExternalStore(assinarSessao, lerSessao, lerSessao);

  /**
   * Verdadeiro enquanto a tentativa de restaurar a sessao esta em curso.
   *
   * Comeca em true, e essa escolha importa: se comecasse em false, o guarda de rota
   * veria "nao autenticado" no primeiro quadro e mandaria para /login todo mundo que
   * apenas recarregou a pagina — inclusive quem tem sessao valida no cookie. O
   * usuario veria a tela de login piscar e desaparecer.
   */
  const [restaurando, definirRestaurando] = useState(true);

  const descartar = useCallback(() => {
    definirSessao(null);

    /*
      O cache carrega dados que o proximo usuario pode nao ter escopo para ver. Sem
      limpar, trocar de conta na mesma aba mostraria por um instante as acoes da
      conta anterior, servidas do cache antes da primeira resposta nova.
    */
    queryClient.clear();
  }, [queryClient]);

  useEffect(() => instalarInterceptorDeSessao(descartar), [descartar]);

  useEffect(() => {
    let vivo = true;

    void restaurarSessao().finally(() => {
      // O guarda evita atualizar estado de um componente ja desmontado, o que
      // acontece no StrictMode e em teste.
      if (vivo) {
        definirRestaurando(false);
      }
    });

    return () => {
      vivo = false;
    };
  }, []);

  const entrar = useCallback(
    async (email: string, senha: string) => {
      // Cache do usuario anterior sai de cena ANTES de a nova sessao valer.
      queryClient.clear();

      const resposta = await entrarNaApi(email, senha);

      // Um login bem sucedido reabilita a renovacao, que pode ter sido desligada
      // por uma recusa anterior nesta mesma aba.
      liberarRenovacao();
      definirSessao(deRespostaDeLogin(resposta));
    },
    [queryClient],
  );

  const sair = useCallback(async () => {
    // A sessao local e descartada primeiro. Se a chamada ao servidor falhar por
    // rede, o usuario ainda precisa sair desta maquina — risco imediato, maior do
    // que o de um refresh token seguir valido no servidor por oito horas.
    descartar();

    try {
      // Sem corpo: quem identifica a sessao e o cookie, e e o servidor que o apaga.
      await sairNaApi();
    } catch {
      // Logout sempre responde 204; chegar aqui significa falha de rede. Nao ha o
      // que informar: para o usuario, a sessao acabou.
    }
  }, [descartar]);

  const valor = useMemo<Sessao>(() => {
    const permissoes = new Set(sessao?.usuario.permissoes ?? []);

    return {
      sessao,
      usuario: sessao?.usuario ?? null,
      autenticado: sessao !== null,
      restaurando,
      deveTrocarSenha: sessao?.deveTrocarSenha ?? false,
      entrar,
      sair,
      temPermissao: (codigo) => permissoes.has(codigo),
    };
  }, [sessao, restaurando, entrar, sair]);

  return <SessaoContexto.Provider value={valor}>{children}</SessaoContexto.Provider>;
}
